import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ErrorMessage } from "formik";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import StaticPopup from "components/StaticPopup";
import LoadingModal from "../LoadingModal";
import SearchableDropdown from "./SearchableDropdown";
import { UNITOPTIONS } from "config";
import { debounce } from "lodash";

const AddMaterialInScope = ({
  materials = [],
  onAddMaterial,
  sectionIndex,
  scopeIndex,
}) => {
  const auth = useSelector((state) => state.auth);
  const user = auth?.user;
  const entityUnits = user?.userEntity?.units || [];

  /* ------------------ STATES ------------------ */
  const [localMaterials, setLocalMaterials] = useState(materials);
  const [isVisible, setIsVisible] = useState(materials.length === 0);
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);

  // Flags
  const localEditLockRef = useRef(false); 
  const lockTimeoutRef = useRef(null);

  /* ------------------ MEMOIZED OPTIONS ------------------ */
  const dynamicUnitOptions = useMemo(() => {
    if (entityUnits.length > 0) {
      return entityUnits.map((unit) => `${unit.code} (${unit.name})`);
    }
    return UNITOPTIONS;
  }, [entityUnits]);

  /* ------------------ UTILITIES ------------------ */
  const isMaterialEmpty = (material) =>
    !material.name &&
    !material.quantity &&
    !material.unit &&
    !material.budget &&
    (!material.brands?.length || material.brands.length === 0) &&
    !material.specifications;

    // at least 1 material detail required 
  // const cleanUpMaterials = (mats) => {
  //   const filtered = mats.filter((m) => !isMaterialEmpty(m));
  //   if (filtered.length === 0) {
  //     return [
  //       { name: "", quantity: "", unit: "", budget: "", brands: [], specifications: "" },
  //     ];
  //   }
  //   return filtered;
  // };

  const cleanUpMaterials = (mats) => {
  return mats.filter((m) => !isMaterialEmpty(m));
};

  /* ------------------ PARENT SYNC (DEBOUNCED) ------------------ */
  const debouncedOnAddMaterial = useMemo(
    () =>
      debounce((updated) => {
        onAddMaterial(updated, scopeIndex);
        // Unlock after debounce duration (300 ms)
        lockTimeoutRef.current = setTimeout(() => {
          localEditLockRef.current = false;
        }, 300);
      }, 300),
    [onAddMaterial, scopeIndex]
  );

  /* ------------------ EFFECTS ------------------ */

  // Sync from parent → child, but ignore when locked
  useEffect(() => {
    if (localEditLockRef.current) return; 

    const parentHasData = Array.isArray(materials) && materials.length > 0;
    if (!parentHasData) return;

    const isDifferent =
      JSON.stringify(materials) !== JSON.stringify(localMaterials);
    if (isDifferent) {
      setLocalMaterials(materials);
      setIsVisible(materials.length === 0);
    }
  }, [materials]);

  // Clean up timer
  useEffect(() => {
    return () => {
      if (lockTimeoutRef.current) clearTimeout(lockTimeoutRef.current);
      debouncedOnAddMaterial.cancel();
    };
  }, [debouncedOnAddMaterial]);

  /* ------------------ HANDLERS ------------------ */
  const handleInputChange = useCallback(
    (index, field, value) => {
      localEditLockRef.current = true; // lock sync
      const updated = localMaterials.map((m, i) =>
        i === index ? { ...m, [field]: value } : m
      );
      const cleaned = cleanUpMaterials(updated);
      setLocalMaterials(cleaned);
      debouncedOnAddMaterial(cleaned);
    },
    [localMaterials, debouncedOnAddMaterial]
  );

  const handleBrandInputChange = useCallback(
    (index, value) => {
      localEditLockRef.current = true;
      const updated = localMaterials.map((m, i) =>
        i === index
          ? { ...m, brands: value.split(",").map((b) => b.trim()) }
          : m
      );
      const cleaned = cleanUpMaterials(updated);
      setLocalMaterials(cleaned);
      debouncedOnAddMaterial(cleaned);
    },
    [localMaterials, debouncedOnAddMaterial]
  );

  const addNewRow = useCallback(() => {
    localEditLockRef.current = true; 
    const newMaterial = {
      name: "",
      quantity: "",
      unit: "",
      budget: "",
      brands: [],
      specifications: "",
    };
    const updated = [...localMaterials, newMaterial];
    setLocalMaterials(updated);
    debouncedOnAddMaterial(updated);
  }, [localMaterials, debouncedOnAddMaterial]);

  const removeRow = useCallback(
    (index) => {
      localEditLockRef.current = true;
      const updated = localMaterials.filter((_, i) => i !== index);
      const cleaned = cleanUpMaterials(updated);
      setLocalMaterials(cleaned);
      debouncedOnAddMaterial(cleaned);
      if (cleaned.length === 0) setIsVisible(true);
    },
    [localMaterials, debouncedOnAddMaterial]
  );

  const handleAddMaterialDetails = useCallback(() => {
    setIsVisible(false);
    if (localMaterials.length === 0) {
      localEditLockRef.current = true; 
      const initial = [
        { name: "", quantity: "", unit: "", budget: "", brands: [], specifications: "" },
      ];
      setLocalMaterials(initial);
      onAddMaterial(initial, scopeIndex);
    }
  }, [localMaterials, onAddMaterial, scopeIndex]);

  /* ------------------ RENDER ------------------ */
  return (
    <>
      {isVisible ? (
        <button
          onClick={handleAddMaterialDetails}
          className="text-xs text-blue-500 underline italic pl-4 pt-4 pb-2"
        >
          Add Material Details
        </button>
      ) : (
        <>
          <div className="relative flex mt-2 pl-2">
            <p className="inline-flex text-xs pb-2 text-gray-600 items-center">
              Material required for work
              <span className="ml-2 relative group bg-gray-200 rounded-full flex items-center justify-center w-4 h-4 text-[10px] cursor-pointer group-hover:text-gray-600">
                i
                <div className="absolute bottom-4 mt-1 hidden w-48 shadow-md border p-2 text-xs text-black bg-white rounded group-hover:block">
                  If there are materials, they need to be provided.
                </div>
              </span>
            </p>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-sm w-full">
              {localMaterials.map((material, index) => (
                <div key={index} className="grid grid-cols-22 gap-x-1 mb-2">
                  <p className="text-center text-xs">
                    M{scopeIndex + 1}.{index + 1}
                  </p>

                  {/* Material Name */}
                  <div className="col-span-8">
                    <ReactQuill
                      value={material.name || ""}
                      onChange={(val) => handleInputChange(index, "name", val)}
                      theme="snow"
                      modules={{ toolbar: false }}
                      className="min-h-[28px] max-h-[60px] p-0 text-sm overflow-y-auto remove-quill-padding"
                    />
                    <ErrorMessage
                      name={`providingFixingDetails[${sectionIndex}].scopes[${scopeIndex}].materials[${index}].name`}
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Quantity */}
                  <div className="col-span-2 self-start flex flex-col justify-end">
                    <input
                      type="text"
                      value={material.quantity || ""}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (
                          /^\d{0,10}(\.\d{0,2})?$/.test(inputValue) ||
                          inputValue === ""
                        ) {
                          handleInputChange(index, "quantity", inputValue);
                        }
                      }}
                      className="border border-[#DCDCD0] p-1 h-[28px] w-full text-sm focus:outline-none focus:ring-0"
                    />
                  </div>

                  {/* Unit */}
                  <div className="col-span-3 flex flex-col justify-end text-sm">
                    <SearchableDropdown
                      value={material.unit}
                      options={dynamicUnitOptions}
                      onChange={(val) => handleInputChange(index, "unit", val)}
                    />
                  </div>

                  {/* Budget */}
                  <div className="col-span-2 self-start flex flex-col justify-end">
                    <input
                      type="text"
                      value={material?.budget || ""}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (
                          /^\d{0,10}(\.\d{0,3})?$/.test(inputValue) ||
                          inputValue === ""
                        ) {
                          handleInputChange(index, "budget", inputValue);
                        }
                      }}
                      className="border border-[#DCDCD0] p-1 h-[28px] w-full text-sm focus:outline-none focus:ring-0"
                    />
                    <ErrorMessage
                      name={`providingFixingDetails[${sectionIndex}].scopes[${scopeIndex}].materials[${index}].budget`}
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  {/* Brands */}
                  <div className="col-span-5 flex flex-col justify-end">
                    <input
                      type="text"
                      value={material.brands.join(", ") || ""}
                      onChange={(e) =>
                        handleBrandInputChange(index, e.target.value)
                      }
                      className="border border-[#DCDCD0] p-2 h-[28px] w-full text-sm focus:outline-none focus:ring-0"
                    />
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-evenly self-end">
                    <button
                      type="button"
                      className="w-fit text-lg"
                      onClick={addNewRow}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className="text-lg"
                    >
                      -
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Loading modal */}
          <LoadingModal
            isOpen={loading || fileError !== ""}
            onClose={() => setFileError(null)}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full border-4 border-t-4 border-orange-500 border-t-gray-200 h-8 w-8 mb-4"></div>
                <p className="text-xl font-medium text-gray-600 italic">
                  Adding materials from file, please wait...
                </p>
              </div>
            ) : (
              <div>
                <p>{fileError || "Materials uploaded successfully!"}</p>
              </div>
            )}
          </LoadingModal>
        </>
      )}
    </>
  );
};

export default AddMaterialInScope;
